import { useMemo, useState } from 'react';
import { Archive, Check, Eye, Info, MessageSquare, RotateCcw, X } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils/cn';
import {
  useApproveCurationMutation,
  useMarkReturnedMutation,
  useMarkUnderReviewMutation,
  usePendingCurationQuery,
  useRejectCurationMutation,
  useRequestFeedbackMutation,
  type PendingCurationItem,
  type PendingStatus,
} from '@/lib/api/promote-back';
import { formatDate } from '@/lib/utils/format';

/**
 * PromoteBackQueue — the curation queue list for Firm Settings →
 * Promote-back queue (T-0111 / E01-F08).
 *
 * Mirrors `design_handoff_caliber_v2/prototype/screens-firm.js →
 * firmQueue()`: an info banner, then a list of `.pb-row` cards (one per
 * pending curation item) with metadata + rationale + Approve / Reject
 * actions. Filter chips (All / Pending / Approved / Rejected) live in
 * the screen header — the screen passes the active filter down here.
 *
 * Authority is firm_admin / curation-permitted partner (ADR-011); 403
 * responses from the approve / reject endpoints surface as inline
 * errors next to the action buttons. Mutations honor an
 * Idempotency-Key per cross-cutting standards so a click-then-network-blip
 * doesn't promote twice.
 *
 * State machine (T-0132 / PR #140):
 *
 *   pending → under_review → reviewer_feedback → returned
 *                  |              |
 *                  +→ approved ←──+→ approved
 *                  +→ rejected ←──+→ rejected
 *
 * `under_review` only from `pending` or `returned`; `reviewer_feedback`
 * requires a non-empty note; `returned` only from `reviewer_feedback`;
 * approve / reject are terminal and idempotent on the same
 * Idempotency-Key. Approved candidates publish as the next template
 * version and notify every engagement currently bound to it.
 */

export type QueueFilter = 'all' | 'pending' | 'decided';

interface PromoteBackQueueProps {
  filter: QueueFilter;
  /** Optional engagement-id filter — when set, items not from this engagement are hidden. */
  sourceEngagementFilter?: string;
}

const TERMINAL_STATUSES: ReadonlySet<PendingStatus> = new Set(['approved', 'rejected']);

export function PromoteBackQueue({ filter, sourceEngagementFilter }: PromoteBackQueueProps) {
  const includeDecided = filter !== 'pending';
  const query = usePendingCurationQuery({ includeDecided });

  const approveMutation = useApproveCurationMutation();
  const rejectMutation = useRejectCurationMutation();
  const underReviewMutation = useMarkUnderReviewMutation();
  const feedbackMutation = useRequestFeedbackMutation();
  const returnMutation = useMarkReturnedMutation();

  // The two textarea-driven flows share a single `forId` / `note` pair so
  // only one panel can be open at a time; the user picks reject or
  // request-feedback per row.
  const [activePanel, setActivePanel] = useState<{
    pendingId: string;
    kind: 'reject' | 'feedback';
  } | null>(null);
  const [note, setNote] = useState('');

  const filtered = useMemo(() => {
    const items = query.data?.items ?? [];
    let result = items;
    if (filter === 'pending') {
      // Treat under_review + reviewer_feedback + returned as "still pending review"
      // for the queue's primary tab.
      result = result.filter((it) => !TERMINAL_STATUSES.has(it.status));
    } else if (filter === 'decided') {
      result = result.filter((it) => TERMINAL_STATUSES.has(it.status));
    }
    if (sourceEngagementFilter) {
      result = result.filter((it) => it.source_engagement_id === sourceEngagementFilter);
    }
    return result;
  }, [query.data, filter, sourceEngagementFilter]);

  const closePanel = () => {
    setActivePanel(null);
    setNote('');
  };

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading promote-back queue…
      </div>
    );
  }
  if (query.error) {
    return <ErrorMessage error={query.error} />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-card border border-ink-100 bg-paper-50 p-3">
        <div className="flex items-start gap-2 text-meta text-ink-700">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-ai-ink" aria-hidden="true" />
          <p className="leading-[18px]">
            Engagement teams can propose changes to firm templates from any register or methodology
            screen. Approved items become the next version of the template and notify every
            engagement currently bound to it. Firm admins and partners allowed to curate decide.
          </p>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No curation items"
          description={
            filter === 'pending'
              ? 'No promote-back proposals are awaiting review. New proposals land here when engagement teams submit them from a register or methodology screen.'
              : 'No curation items match the current filter.'
          }
        />
      ) : (
        <ul className="space-y-3" role="list">
          {filtered.map((item) => (
            <QueueRow
              key={item.id}
              item={item}
              isMutating={
                approveMutation.isPending ||
                rejectMutation.isPending ||
                underReviewMutation.isPending ||
                feedbackMutation.isPending ||
                returnMutation.isPending
              }
              actionError={
                approveMutation.error ??
                rejectMutation.error ??
                underReviewMutation.error ??
                feedbackMutation.error ??
                returnMutation.error
              }
              activePanel={activePanel?.pendingId === item.id ? activePanel.kind : null}
              note={activePanel?.pendingId === item.id ? note : ''}
              setNote={setNote}
              onApprove={() => {
                approveMutation.mutate({
                  pendingId: item.id,
                  idempotencyKey: `curation-approve:${item.id}`,
                });
              }}
              onMarkUnderReview={() => {
                underReviewMutation.mutate({
                  pendingId: item.id,
                  idempotencyKey: `curation-under-review:${item.id}`,
                });
              }}
              onMarkReturned={() => {
                returnMutation.mutate({
                  pendingId: item.id,
                  idempotencyKey: `curation-return:${item.id}`,
                });
              }}
              onOpenReject={() => {
                setActivePanel({ pendingId: item.id, kind: 'reject' });
                setNote('');
              }}
              onOpenFeedback={() => {
                setActivePanel({ pendingId: item.id, kind: 'feedback' });
                setNote('');
              }}
              onCancelPanel={closePanel}
              onConfirmReject={() => {
                if (note.trim().length === 0) return;
                rejectMutation.mutate(
                  {
                    pendingId: item.id,
                    input: { decision_note: note.trim() },
                    idempotencyKey: `curation-reject:${item.id}`,
                  },
                  { onSuccess: closePanel },
                );
              }}
              onConfirmFeedback={() => {
                if (note.trim().length === 0) return;
                feedbackMutation.mutate(
                  {
                    pendingId: item.id,
                    input: { decision_note: note.trim() },
                    idempotencyKey: `curation-feedback:${item.id}`,
                  },
                  { onSuccess: closePanel },
                );
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface QueueRowProps {
  item: PendingCurationItem;
  isMutating: boolean;
  actionError: unknown;
  activePanel: 'reject' | 'feedback' | null;
  note: string;
  setNote: (s: string) => void;
  onApprove: () => void;
  onMarkUnderReview: () => void;
  onMarkReturned: () => void;
  onOpenReject: () => void;
  onOpenFeedback: () => void;
  onCancelPanel: () => void;
  onConfirmReject: () => void;
  onConfirmFeedback: () => void;
}

const STATUS_LEFT_BORDER: Partial<Record<PendingStatus, string>> = {
  approved: 'border-l-[3px] border-l-posture-healthy',
  rejected: 'border-l-[3px] border-l-posture-critical',
  under_review: 'border-l-[3px] border-l-ai-ink',
  reviewer_feedback: 'border-l-[3px] border-l-posture-at-risk',
  returned: 'border-l-[3px] border-l-accent-amber-600',
};

function QueueRow({
  item,
  isMutating,
  actionError,
  activePanel,
  note,
  setNote,
  onApprove,
  onMarkUnderReview,
  onMarkReturned,
  onOpenReject,
  onOpenFeedback,
  onCancelPanel,
  onConfirmReject,
  onConfirmFeedback,
}: QueueRowProps) {
  const isPanelOpen = activePanel !== null;
  // Action affordances per state (T-0132 transitions):
  //   pending           → under_review (curator open), request feedback, approve, reject
  //   under_review      → request feedback, approve, reject
  //   reviewer_feedback → return (submitter ack), approve, reject
  //   returned          → under_review (curator re-open), request feedback, approve, reject
  //   approved / rejected → terminal, no actions
  const canMarkUnderReview = item.status === 'pending' || item.status === 'returned';
  const canRequestFeedback =
    item.status === 'pending' || item.status === 'under_review' || item.status === 'returned';
  const canMarkReturned = item.status === 'reviewer_feedback';
  const canDecide = !TERMINAL_STATUSES.has(item.status);

  return (
    <li
      className={cn(
        'flex flex-wrap items-start gap-4 rounded-card border border-ink-100 bg-paper-0 p-4',
        STATUS_LEFT_BORDER[item.status],
      )}
    >
      <div
        aria-hidden="true"
        className="flex h-9 w-9 flex-none items-center justify-center rounded-card bg-paper-100 text-ink-700"
      >
        <Archive className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-meta text-ink-500">
            {item.id.slice(0, 8).toUpperCase()}
          </span>
          <h3 className="text-body font-semibold text-ink-900">{item.candidate_name}</h3>
          <StatusChip status={item.status} />
        </div>
        <div className="mt-1 text-meta text-ink-500">
          From{' '}
          <Link
            to="/engagements/$engagementId"
            params={{ engagementId: item.source_engagement_id }}
            className="text-ink-700 underline-offset-[3px] hover:underline"
          >
            engagement {item.source_engagement_id.slice(0, 8)}
          </Link>
          {' · '}
          Submitted by <span className="font-mono">{item.submitted_by.slice(0, 8)}</span> on{' '}
          {formatDate(item.submitted_at)}
        </div>

        {TERMINAL_STATUSES.has(item.status) && item.decided_at ? (
          <div className="mt-1 text-meta text-ink-500">
            Decided by{' '}
            <span className="font-mono">
              {item.decided_by ? item.decided_by.slice(0, 8) : 'unknown'}
            </span>{' '}
            on {formatDate(item.decided_at)}
            {item.status === 'approved' && item.resulting_template_id ? (
              <>
                {' · resulted in '}
                <span className="font-mono text-ink-700">
                  template {item.resulting_template_id.slice(0, 8)}
                </span>{' '}
                v{item.resulting_template_version}
              </>
            ) : null}
          </div>
        ) : null}

        {item.decision_note ? (
          <div
            className={cn(
              'mt-3 rounded-card p-2.5 text-meta',
              item.status === 'rejected'
                ? 'border border-posture-critical-tint bg-posture-critical-tint/40 text-posture-critical'
                : item.status === 'reviewer_feedback'
                  ? 'border border-posture-at-risk-tint bg-posture-at-risk-tint/40 text-posture-at-risk'
                  : 'border border-ink-100 bg-paper-50 text-ink-700',
            )}
          >
            <span className="font-semibold">
              {item.status === 'rejected'
                ? 'Reject reason'
                : item.status === 'reviewer_feedback'
                  ? 'Reviewer feedback'
                  : 'Note'}
              :
            </span>{' '}
            {item.decision_note}
          </div>
        ) : null}

        {isPanelOpen ? (
          <div className="mt-3 rounded-card border border-ink-200 bg-paper-50 p-3">
            <label
              htmlFor={`note-${item.id}`}
              className="block text-meta font-semibold text-ink-700"
            >
              {activePanel === 'reject' ? 'Reject reason' : 'Reviewer feedback'}
            </label>
            <textarea
              id={`note-${item.id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                activePanel === 'reject'
                  ? 'Required — what made this candidate unfit for promotion?'
                  : 'Required — what should the submitter revise?'
              }
              rows={3}
              className="mt-1.5 w-full rounded border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-700 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
            />
            <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
              {actionError ? <ErrorMessage error={actionError} className="w-full" /> : null}
              <Button type="button" variant="ghost" size="sm" onClick={onCancelPanel}>
                Cancel
              </Button>
              {activePanel === 'reject' ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={isMutating || note.trim().length === 0}
                  onClick={onConfirmReject}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                  {isMutating ? 'Rejecting…' : 'Confirm reject'}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  disabled={isMutating || note.trim().length === 0}
                  onClick={onConfirmFeedback}
                >
                  <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                  {isMutating ? 'Sending…' : 'Request feedback'}
                </Button>
              )}
            </div>
          </div>
        ) : null}

        {actionError && !isPanelOpen ? (
          <div className="mt-3">
            <ErrorMessage error={actionError} />
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap flex-none items-center gap-2 self-start">
        {canDecide && !isPanelOpen ? (
          <>
            {canMarkUnderReview && item.status !== 'under_review' ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isMutating}
                onClick={onMarkUnderReview}
                title="Curator opened the candidate for review."
              >
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                {item.status === 'returned' ? 'Re-open' : 'Open for review'}
              </Button>
            ) : null}
            {canRequestFeedback ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isMutating}
                onClick={onOpenFeedback}
                title="Ask the submitter to revise — requires a note."
              >
                <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                Request feedback
              </Button>
            ) : null}
            {canMarkReturned ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isMutating}
                onClick={onMarkReturned}
                title="Submitter acknowledged — return to the curator queue."
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                Mark returned
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isMutating}
              onClick={onOpenReject}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              Reject
            </Button>
            <Button type="button" size="sm" disabled={isMutating} onClick={onApprove}>
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              {isMutating ? 'Saving…' : 'Approve'}
            </Button>
          </>
        ) : null}
      </div>
    </li>
  );
}

function StatusChip({ status }: { status: PendingStatus }) {
  switch (status) {
    case 'approved':
      return (
        <span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-posture-healthy-tint px-2.5 text-meta font-medium text-posture-healthy">
          <Check className="h-3 w-3" aria-hidden="true" /> Approved
        </span>
      );
    case 'rejected':
      return (
        <span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-posture-critical-tint px-2.5 text-meta font-medium text-posture-critical">
          <X className="h-3 w-3" aria-hidden="true" /> Rejected
        </span>
      );
    case 'under_review':
      return (
        <span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-ai-surface px-2.5 text-meta font-medium text-ai-ink">
          <Eye className="h-3 w-3" aria-hidden="true" /> Under review
        </span>
      );
    case 'reviewer_feedback':
      return (
        <span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-posture-at-risk-tint px-2.5 text-meta font-medium text-posture-at-risk">
          <MessageSquare className="h-3 w-3" aria-hidden="true" /> Reviewer feedback
        </span>
      );
    case 'returned':
      return (
        <span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-paper-100 px-2.5 text-meta font-medium text-accent-amber-700">
          <RotateCcw className="h-3 w-3" aria-hidden="true" /> Returned
        </span>
      );
    case 'pending':
    default:
      return (
        <span className="inline-flex h-[22px] items-center rounded-full bg-ai-surface px-2.5 text-meta font-medium text-ai-ink">
          Awaiting review
        </span>
      );
  }
}
